import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { Building2, ChevronLeft, ChevronRight, Search, Sparkles, Star, UserRound, X } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { apiErrorMessage, MAX_RECIPIENTS, type DirectoryEntry, type ReferralTemplate } from '../api/referralsApi';
import { useDeliveryOptions, useDirectorySearch, useReferralMutations, useSuggestedRecipients } from '../hooks';
import styles from './RecipientPicker.module.css';

interface RecipientPickerProps {
  template: ReferralTemplate;
  /** The offices on the slip so far, in order. */
  selected: DirectoryEntry[];
  onAdd: (entry: DirectoryEntry) => void;
  onRemove: (entryId: string) => void;
  /** An office's details changed (e.g. an email was added): swap the entry in place. */
  onReplace?: (entry: DirectoryEntry) => void;
  /** What the sender has filled in so far; narrows the suggestions. */
  reasons: string[];
  comment: string;
  senderCity?: string | null;
  disabled?: boolean;
  /** Open the details modal for this office (e.g. "Add their email" from the preview). */
  editDetailsFor?: DirectoryEntry | null;
  /** Called when the details modal closes; `saved` is set when the office was updated. */
  onEditDetailsDone?: (saved?: DirectoryEntry) => void;
}

const PAGE_SIZE = 4;

/**
 * "Dental office" / "Practice" field: type a phone number (best) or a name; pick from the directory,
 * PracticeNova customers and Google Maps; or add an office by hand. Below it, the "Suggested in
 * <city>" strip ranks nearby specialists for what this slip is about. A slip can offer the patient
 * up to MAX_RECIPIENTS offices; each gets its own copy and link.
 */
export function RecipientPicker({
  template,
  selected,
  onAdd,
  onRemove,
  onReplace,
  reasons,
  comment,
  senderCity,
  disabled,
  editDetailsFor,
  onEditDetailsDone,
}: RecipientPickerProps) {
  const toast = useToast();
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 300);
  const [specialtyFilter, setSpecialtyFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [adding, setAdding] = useState<Partial<DirectoryEntry> | null>(null);
  const [open, setOpen] = useState(false);
  const search = useDirectorySearch(debounced);
  const debouncedComment = useDebounce(comment, 700);
  const suggested = useSuggestedRecipients({ specialty: specialtyFilter || undefined, reasons, comment: debouncedComment });
  const { createDirectoryPractice } = useReferralMutations();

  useEffect(() => setPage(0), [suggested.data?.specialty, specialtyFilter]);
  useEffect(() => {
    if (editDetailsFor) setAdding(editDetailsFor);
  }, [editDetailsFor]);

  const chosenIds = new Set(selected.map((e) => e.id));
  const notChosen = (e: DirectoryEntry) => !e.id || !chosenIds.has(e.id);
  const canAddMore = selected.length < MAX_RECIPIENTS && !disabled;
  const results = (search.data ?? []).filter(notChosen);
  const entries = (suggested.data?.entries ?? []).filter(notChosen);
  const frequent = (suggested.data?.frequent ?? []).filter(notChosen);
  const hasSignal = suggested.data ? suggested.data.basis !== 'none' : false;
  const pages = Math.max(1, Math.ceil(entries.length / PAGE_SIZE));
  const pageEntries = entries.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const specialtyOptions = useMemo(
    () => [{ value: '', label: 'Choose a specialty…' }, ...template.specialties.map((s) => ({ value: s.value, label: s.label }))],
    [template.specialties],
  );

  /** A Google Maps hit has no directory row yet; make one so the slip can point at it. */
  const choose = async (entry: DirectoryEntry) => {
    if (entry.id) {
      onAdd(entry);
      setOpen(false);
      setQuery('');
      return;
    }
    try {
      const created = await createDirectoryPractice.mutateAsync({
        name: entry.name,
        phone: entry.phone,
        website: entry.website,
        address: entry.address,
        city: entry.city,
        state: entry.state,
        zip: entry.zip,
        specialty: entry.specialty ?? suggested.data?.specialty ?? null,
        googlePlaceId: entry.googlePlaceId,
      });
      onAdd(created);
      setOpen(false);
      setQuery('');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not add that office'));
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="referral-recipient">
          {template.vertical === 'dental' ? 'Dental office' : 'Practice'}
        </label>
        {selected.map((office, i) => (
          <SelectedOffice
            key={office.id ?? office.name}
            office={office}
            index={i}
            total={selected.length}
            onEdit={() => setAdding(office)}
            onRemove={disabled ? undefined : () => office.id && onRemove(office.id)}
          />
        ))}
        {selected.length > 0 && selected.length < MAX_RECIPIENTS && (
          <p className={styles.tip}>
            {selected.length === 1
              ? 'Give the patient a choice: add a second or third office and they can book with whichever fits. Each office gets its own copy and secure link; the first to confirm takes the referral.'
              : `Up to ${MAX_RECIPIENTS} offices per referral.`}
          </p>
        )}
        {canAddMore && (
          <div className={styles.searchWrap}>
            <Input
              id="referral-recipient"
              fullWidth
              leftIcon={<Search size={14} />}
              placeholder={selected.length ? 'Add another office (optional)' : 'struck by whitening, john doe, (111) 222 3333'}
              value={query}
              disabled={disabled}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              autoComplete="off"
            />
            <p className={styles.tip}>
              Tip: <strong>Office phone number</strong> search yields the best results
            </p>
            {open && debounced.trim().length >= 2 && (
              <div className={styles.dropdown} role="listbox">
                {search.isLoading && <div className={styles.dropdownNote}>Searching…</div>}
                {!search.isLoading && results.length === 0 && <div className={styles.dropdownNote}>No office found for “{debounced}”.</div>}
                {results.map((r) => (
                  <button
                    key={r.id ?? r.googlePlaceId ?? r.name}
                    type="button"
                    className={styles.option}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => void choose(r)}
                    role="option"
                    aria-selected={false}
                  >
                    <span className={styles.optionName}>
                      {r.name}
                      {r.onPracticeNova && <span className={styles.pnBadge}>PN</span>}
                      {r.source === 'places' && <span className={styles.srcBadge}>Google Maps</span>}
                    </span>
                    <span className={styles.optionMeta}>
                      {[r.specialtyLabel, r.phone, [r.city, r.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                ))}
                <button
                  type="button"
                  className={clsx(styles.option, styles.optionAdd)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const digits = debounced.replace(/\D/g, '');
                    setAdding(digits.length >= 7 ? { phone: debounced } : { name: debounced });
                    setOpen(false);
                  }}
                >
                  + Add a new practice
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {canAddMore && frequent.length > 0 && (
        <section className={styles.suggested} aria-label="Offices you refer to">
          <div className={styles.suggestedHeader}>
            <h3 className={styles.suggestedTitle}>
              <Star size={15} className={styles.whyStar} />
              Offices you refer to
            </h3>
          </div>
          <div className={styles.cards}>
            {frequent.map((e) => (
              <SuggestedCard key={e.id ?? e.name} entry={e} onPick={() => void choose(e)} busy={createDirectoryPractice.isPending} />
            ))}
          </div>
        </section>
      )}

      {canAddMore && (
        <section className={styles.suggested} aria-label="Suggested offices">
          <div className={styles.suggestedHeader}>
            <h3 className={styles.suggestedTitle}>
              <Sparkles size={15} className={styles.sparkle} />
              {hasSignal && suggested.data?.specialtyLabel
                ? `Nearby: ${suggested.data.specialtyLabel}${senderCity ? ` near ${senderCity}` : ''}`
                : 'Suggested offices'}
              {suggested.data?.basis === 'inferred' && <span className={styles.suggestedSub}>Based on this slip's reasons</span>}
            </h3>
            <div className={styles.suggestedControls}>
              <Select
                aria-label="Specialty"
                options={specialtyOptions}
                value={specialtyFilter}
                onChange={(e) => setSpecialtyFilter(e.target.value)}
                className={styles.specialtySelect}
              />
              {hasSignal && pages > 1 && (
                <span className={styles.pager}>
                  <button type="button" className={styles.pagerBtn} onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} aria-label="Previous">
                    <ChevronLeft size={14} />
                  </button>
                  {page + 1} of {pages}
                  <button type="button" className={styles.pagerBtn} onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} aria-label="Next">
                    <ChevronRight size={14} />
                  </button>
                </span>
              )}
            </div>
          </div>
          {suggested.isLoading ? (
            <div className={styles.cards}>
              <Skeleton height={96} borderRadius="var(--card-radius)" />
              <Skeleton height={96} borderRadius="var(--card-radius)" />
            </div>
          ) : !hasSignal ? (
            <p className={styles.emptyNote}>
              Tell us what this referral is for — pick a reason below, jot a note in the comment, or choose a specialty above — and
              we'll suggest nearby {template.vertical === 'dental' ? 'specialists' : 'practices'} that fit. Or search by the office's phone number.
            </p>
          ) : entries.length === 0 ? (
            <p className={styles.emptyNote}>
              No {suggested.data?.specialtyLabel?.toLowerCase() ?? 'matching'} offices found within 15 miles. Search by phone number above, or add the
              office you use — it will be suggested next time.
            </p>
          ) : (
            <div className={styles.cards}>
              {pageEntries.map((e) => (
                <SuggestedCard key={e.id ?? e.googlePlaceId ?? e.name} entry={e} onPick={() => void choose(e)} busy={createDirectoryPractice.isPending} />
              ))}
            </div>
          )}
        </section>
      )}

      {adding && (
        <AddPracticeModal
          template={template}
          initial={adding}
          onClose={() => {
            setAdding(null);
            onEditDetailsDone?.();
          }}
          onSaved={(entry) => {
            setAdding(null);
            if (entry.id && chosenIds.has(entry.id)) onReplace?.(entry);
            else onAdd(entry);
            onEditDetailsDone?.(entry);
          }}
        />
      )}
    </div>
  );
}

function SelectedOffice({
  office,
  index,
  total,
  onEdit,
  onRemove,
}: {
  office: DirectoryEntry;
  index: number;
  total: number;
  onEdit: () => void;
  onRemove?: () => void;
}) {
  const delivery = useDeliveryOptions(office.id);
  return (
    <div className={styles.selected}>
      <div className={styles.selectedMain}>
        {total > 1 ? <span className={styles.position}>{index + 1}</span> : <Building2 size={18} className={styles.selectedIcon} />}
        <div>
          <div className={styles.selectedName}>
            {office.name}
            {office.onPracticeNova && <span className={styles.pnBadge}>On PracticeNova</span>}
          </div>
          <div className={styles.selectedMeta}>
            {[office.specialtyLabel, office.phone, [office.city, office.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
          </div>
          {!office.email && !office.onPracticeNova && (
            <div className={styles.note}>
              <DeliveryNote delivery={delivery.data ?? null} loading={delivery.isLoading} onAddDetails={onEdit} />
            </div>
          )}
          {office.acceptingNewPatients === false && <div className={styles.warn}>Not accepting new patients right now.</div>}
          {office.intakeInstructions && <div className={styles.intake}>{office.intakeInstructions}</div>}
        </div>
      </div>
      {onRemove && (
        <button type="button" className={styles.clear} onClick={onRemove} aria-label={`Remove ${office.name}`}>
          <X size={16} />
        </button>
      )}
    </div>
  );
}

/**
 * What "no email" means for this office, stated from what we actually know: whether its number can
 * receive texts (carrier lookup, or a mobile the office gave us), whether the sender's own texting
 * number is live, and that a printed slip always works.
 */
function DeliveryNote({
  delivery,
  loading,
  onAddDetails,
}: {
  delivery: { text: { available: boolean; to: string | null; lineType: string | null; from: string | null; reason: string | null } } | null;
  loading: boolean;
  onAddDetails: () => void;
}) {
  if (loading || !delivery) return <>No email on file for this office. Checking how else it can be reached…</>;
  const t = delivery.text;
  return (
    <>
      <strong>No email on file</strong>, so this office cannot get the instant secure link.{' '}
      {t.available ? (
        <>
          When you send, we can text the secure link to {t.to}
          {t.lineType === 'mobile' ? ' (a mobile number)' : t.lineType === 'given' ? ' (the mobile you gave us)' : ' (an internet line; some do not receive texts)'} from your texting
          number{t.from ? ` ${t.from}` : ''}, alongside the referral.
        </>
      ) : (
        <>{t.reason ?? 'It cannot be texted.'}</>
      )}{' '}
      A printed slip with a QR code to the referral is always available at Preview &amp; Send.{' '}
      <button type="button" className={styles.linkBtn} onClick={onAddDetails}>
        Add their email{t.available ? '' : ' or a mobile number'}
      </button>
    </>
  );
}

function SuggestedCard({ entry, onPick, busy }: { entry: DirectoryEntry; onPick: () => void; busy: boolean }) {
  return (
    <button type="button" className={styles.card} onClick={onPick} disabled={busy}>
      <div className={styles.cardLogo}>
        <Building2 size={20} />
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardName}>
          {entry.name}
          {entry.onPracticeNova && <span className={styles.pnBadge}>On PracticeNova</span>}
        </div>
        {entry.specialtyLabel && <div className={styles.cardSpecialty}>{entry.specialtyLabel}</div>}
        <div className={styles.cardMeta}>
          {[entry.phone, [entry.city, entry.state].filter(Boolean).join(', '), entry.distanceMiles != null ? `${entry.distanceMiles.toFixed(1)}mi` : null]
            .filter(Boolean)
            .join(' • ')}
        </div>
        {entry.providers.length > 0 && (
          <div className={styles.providers}>
            {entry.providers.slice(0, 3).map((p) => (
              <span key={p.id} className={styles.provider}>
                <UserRound size={11} /> {p.name}
              </span>
            ))}
          </div>
        )}
        {entry.why.length > 0 && (
          <div className={styles.why}>
            {entry.priorReferrals > 0 && <Star size={11} className={styles.whyStar} />}
            {entry.why.slice(0, 2).join(' · ')}
          </div>
        )}
      </div>
    </button>
  );
}

function AddPracticeModal({
  template,
  initial,
  onClose,
  onSaved,
}: {
  template: ReferralTemplate;
  initial: Partial<DirectoryEntry>;
  onClose: () => void;
  onSaved: (entry: DirectoryEntry) => void;
}) {
  const toast = useToast();
  const { createDirectoryPractice, updateDirectoryPractice, locationId } = useReferralMutations();
  const [form, setForm] = useState({
    name: initial.name ?? '',
    phone: initial.phone ?? '',
    smsPhone: initial.smsPhone ?? '',
    email: initial.email ?? '',
    address: initial.address ?? '',
    city: initial.city ?? '',
    state: initial.state ?? '',
    zip: initial.zip ?? '',
    specialty: initial.specialty ?? '',
    practiceType: (initial.practiceType ?? 'specialist') as 'generalist' | 'specialist',
    providers: '',
  });
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const email = form.email.trim();
  const emailOk = !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSave = form.name.trim().length >= 2 && emailOk;

  const save = async () => {
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        smsPhone: form.smsPhone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        zip: form.zip.trim() || null,
        specialty: form.specialty || null,
        practiceType: form.practiceType,
        googlePlaceId: initial.googlePlaceId ?? null,
      };
      const providers = form.providers
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean);
      const entry = initial.id
        ? await updateDirectoryPractice.mutateAsync({ id: initial.id, input: payload })
        : await createDirectoryPractice.mutateAsync({ ...payload, providers });
      onSaved(entry);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save the office'));
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={initial.id ? 'Complete office details' : 'Add a new practice'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={!canSave || !locationId} loading={createDirectoryPractice.isPending || updateDirectoryPractice.isPending}>
            Save office
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Input label="Practice name" fullWidth value={form.name} onChange={(e) => set('name', e.target.value)} required />
        <div className={styles.formRow}>
          <Input label="Office phone" fullWidth value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(925) 555-0100" />
          <Input
            label="Email (optional)"
            fullWidth
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            error={emailOk ? undefined : 'That does not look like an email address.'}
            hint="With an email the office gets a secure link the moment you send. Without one, you can text their office phone or print the slip."
          />
        </div>
        <Input
          label="Mobile number for texts (optional)"
          fullWidth
          value={form.smsPhone}
          onChange={(e) => set('smsPhone', e.target.value)}
          placeholder="(925) 555-0199"
          hint="Office main lines are usually landlines and cannot receive texts. If you know a number this office texts from, the referral's secure link can go there."
        />
        <Input label="Address" fullWidth value={form.address} onChange={(e) => set('address', e.target.value)} />
        <div className={styles.formRow3}>
          <Input label="City" fullWidth value={form.city} onChange={(e) => set('city', e.target.value)} />
          <Input label="State" fullWidth value={form.state} onChange={(e) => set('state', e.target.value)} maxLength={2} />
          <Input label="ZIP" fullWidth value={form.zip} onChange={(e) => set('zip', e.target.value)} />
        </div>
        <div className={styles.formRow}>
          <Select
            label="Type of practice"
            fullWidth
            options={[
              { value: 'specialist', label: 'Specialist' },
              { value: 'generalist', label: template.vertical === 'dental' ? 'General dentist' : 'Primary care' },
            ]}
            value={form.practiceType}
            onChange={(e) => set('practiceType', e.target.value)}
          />
          <Select
            label="Specialty"
            fullWidth
            options={[{ value: '', label: 'Not sure' }, ...template.specialties]}
            value={form.specialty}
            onChange={(e) => set('specialty', e.target.value)}
          />
        </div>
        <Input
          label="Doctors at this office (optional, comma-separated)"
          fullWidth
          value={form.providers}
          onChange={(e) => set('providers', e.target.value)}
          placeholder="Dr. B Hoch, Dr. S Hoch"
        />
      </div>
    </Modal>
  );
}
